import { useState } from "react";
import { Container, Card, Button, Row, Col, Badge, Form, InputGroup, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserProfile = ({ auth, onLogout }) => {
    const user = auth.user;
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    if (!user) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <h3>No user data available</h3>
                    <Link to="/" className="btn btn-primary mt-3">Return to Home</Link>
                </div>
            </Container>
        );
    }

    const renderRoleBadge = (role) => {
        switch (role) {
            case "admin":
                return <Badge bg="danger">{role}</Badge>;
            default:
                return <Badge bg="primary">{role}</Badge>;
        }
    };

    const validatePassword = (password) => {
        if (!password) {
            return "Password is required";
        }

        if (password.length < 12 || password.length > 128) {
            return "Password must be 12-128 characters long";
        }

        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const criteriaMet = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar].filter(Boolean).length;

        if (criteriaMet < 3) {
            return "Password must include at least 3 of: uppercase, lowercase, number, special character";
        }

        return "";
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const passwordValidationError = validatePassword(newPassword);

        if (passwordValidationError) {
            setPasswordError(passwordValidationError);
            setIsLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError("Passwords do not match");
            setIsLoading(false);
            return;
        }

        try {
            await axios.put(`${API_BASE_URL}/auth/change-password`, { newPassword },
                {headers: { "Content-Type": "application/json", "Authorization": `Bearer ${auth.token}`}}
            );

            setSuccessMessage("Password changed successfully!");
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");

            // Close modal after 3 seconds
            setTimeout(() => {
                setShowChangePasswordModal(false);
                setSuccessMessage("");
            }, 3000);
        } catch (err) {
            setPasswordError(err.response?.data?.message || "Failed to change password");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleChangePasswordModal = () => {
        setShowChangePasswordModal(!showChangePasswordModal);
        // Clear form and errors when closing
        if (showChangePasswordModal) {
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");
            setSuccessMessage("");
        }
    };

    return (
        <>
            <Row className="justify-content-center mt-5">
                <Col md={8} lg={6}>
                    <Card className="shadow-sm border-0 bg-light">
                        <Card.Body className="p-4">
                            <div className="text-center mb-4">
                                <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto" style={{ width: "10rem", height: "10rem", fontSize: "2.5rem" }}>
                                    {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                                </div>
                                <h2 className="mt-3">{user.name || "User Profile"}</h2>
                            </div>
                            <div className="mb-3">
                                <strong>Name:</strong> {user.name || "N/A"}
                            </div>
                            <div className="mb-3">
                                <strong>Email:</strong> {user.email}
                            </div>
                            <div className="mb-3">
                                <strong>Role:</strong>{" "}
                                {renderRoleBadge(user.role)}
                            </div>
                            <div className="d-flex justify-content-between mt-4">
                                <Link to="/" className="btn btn-secondary"> Back to Home </Link>
                                <Button variant="primary" onClick={toggleChangePasswordModal}> Change Password </Button>
                                <Button variant="danger" onClick={onLogout}>
                                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                                    Log Out
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Change Password Modal */}
            <Modal show={showChangePasswordModal} onHide={toggleChangePasswordModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handlePasswordChange}>
                        <Form.Group className="mb-3">
                            <InputGroup>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    placeholder="New Password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    isInvalid={!!passwordError}
                                    required
                                />
                                <InputGroup.Text
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </InputGroup.Text>
                            </InputGroup>
                            <Form.Text muted> Must be 12-128 characters with at least 3 of: uppercase, lowercase, number, and special character </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type={showPassword ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                isInvalid={!!passwordError}
                                required
                            />
                        </Form.Group>
                        {passwordError && (
                            <div className="text-danger mb-3">{passwordError}</div>
                        )}
                        {successMessage && (
                            <div className="text-success mb-3">{successMessage}</div>
                        )}
                        <Button variant="primary" type="submit" disabled={isLoading}>
                            {isLoading ? "Updating..." : "Update Password"}
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={toggleChangePasswordModal}> Cancel </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserProfile;